import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';

import api from '../../utils/webcall';
import { VisuallyHiddenInput, getErrorMessage } from '../../utils/utils';
import AccessController from '../../components/access_controller';
import UserProfile from '../../components/User';
import { getVizTokens } from './vizTokens';
import {
    ChartCard,
    EmptyState,
    Legend,
    RankedBarList,
    StatTile,
    formatCurrency,
    formatFullCurrency,
} from './components';
import { FindingsList, MoneyWaterfall, SectionHeader } from './sections';

const RANGE_PRESETS = [
    { label: '30 days', days: 30 },
    { label: '90 days', days: 90 },
    { label: '1 year', days: 365 },
];

const PLATFORMS = ['Zomato', 'Swiggy', 'Ownly Food'];

// Ownly Food ships .xlsx; the other two are CSV order reports.
const ORDER_FILE_TYPES = {
    'Zomato': '.csv', 'Swiggy': '.csv', 'Ownly Food': '.xlsx,.xls',
};

function isoDaysAgo(days) {
    const date = new Date();
    date.setDate(date.getDate() - (days - 1));
    return date.toISOString().slice(0, 10);
}

function todayIso() {
    return new Date().toISOString().slice(0, 10);
}

/**
 * Width a low-cardinality category chart should be allowed to occupy.
 *
 * MUI x-charts sizes bars as a proportion of the band, so a chart with a few
 * categories stretched across a wide monitor produces very thick bars. Capping
 * the plot keeps marks thin at any screen size and avoids a sparse, stretched
 * chart. Roughly one band's worth per category, plus room for the axis.
 */
function categoryChartWidth(count) {
    // ~90px per band plus the axis gutter. Paired with CATEGORY_GAP below this
    // keeps bars near 20px: band 90-110px x (1 - 0.8) = 18-22px.
    return Math.max(300, count * 90 + 140);
}

// High on purpose - the leftover band width is meant to be air, not mark.
const CATEGORY_GAP = 0.8;

/** "2026-03-13" -> "13 Mar", for axis ticks. */
function shortDate(iso) {
    const [, month, day] = iso.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${parseInt(day, 10)} ${months[parseInt(month, 10) - 1]}`;
}

/** Shared upload modal for both order reports and payout statements. */
function UploadForm(props) {
    const { kind, showErrorMessage, showSuccessMessage, onUploaded, onClose } = props;
    const isMobile = useMediaQuery('(max-width:600px)');
    const isPayout = kind === 'payout';
    const [platform, setPlatform] = useState('Zomato');
    const [files, setFiles] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    const endpoint = isPayout ? '/pos/payouts/upload/' : '/pos/orders/upload/';
    const accept = isPayout ? '.xlsx,.xls' : (ORDER_FILE_TYPES[platform] || '.csv');

    const handleSubmit = () => {
        setSubmitting(true);
        setResult(null);
        const form = new FormData();
        form.append('platform', platform);
        Array.from(files).forEach((file) => form.append(file.name, file));

        api.post(endpoint, form).then((res) => {
            setResult(res.data);
            showSuccessMessage(
                `Ingested ${res.data.created || 0} new and updated ${res.data.updated || 0} rows.`);
            if (onUploaded) onUploaded();
        }).catch((error) => {
            showErrorMessage(getErrorMessage(error));
        }).finally(() => setSubmitting(false));
    };

    return (
        <Box sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: isMobile ? '90%' : 460, maxWidth: 460, maxHeight: '85vh',
            overflowY: 'auto', bgcolor: 'background.paper', borderRadius: 1,
            boxShadow: 24, p: isMobile ? 2 : 3,
        }}>
            <Grid container direction="column" rowSpacing={2}>
                <Grid>
                    <Typography variant="h6" component="h2">
                        {isPayout ? 'Upload payout statement' : 'Upload order report'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {isPayout
                            ? 'The payout statement from the platform finance section - this is what tells you what actually reached the bank.'
                            : 'The order-level report from the platform partner dashboard.'}
                        {' '}Re-uploading an overlapping file updates existing rows
                        rather than duplicating them.
                    </Typography>
                </Grid>
                <Grid>
                    <FormControl fullWidth size="small">
                        <InputLabel id="upload-platform-label">Platform</InputLabel>
                        <Select labelId="upload-platform-label" label="Platform"
                            value={platform} onChange={(e) => setPlatform(e.target.value)}>
                            {PLATFORMS.map((name) => (
                                <MenuItem key={name} value={name}>{name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid>
                    <Button component="label" variant="outlined" fullWidth>
                        Choose file(s)
                        <VisuallyHiddenInput type="file" accept={accept} multiple
                            onChange={(e) => { setFiles(e.target.files); setResult(null); }} />
                    </Button>
                </Grid>
                {files && (
                    <Grid>
                        {Array.from(files).map((f, i) => (
                            <Alert severity="info" sx={{ mb: 0.5 }} key={`f-${i}`}>{f.name}</Alert>
                        ))}
                    </Grid>
                )}
                <Grid>
                    <Button variant="contained" fullWidth disabled={!files || submitting}
                        onClick={handleSubmit}>
                        {submitting ? 'Uploading...' : 'Submit'}
                    </Button>
                </Grid>
                {result && (
                    <Grid>
                        <Alert severity={result.errors && result.errors.length ? 'warning' : 'success'}>
                            {result.created} created, {result.updated} updated.
                            {result.errors && result.errors.length > 0 &&
                                ` ${result.errors.length} row(s) skipped.`}
                        </Alert>
                    </Grid>
                )}
                <Grid><Button fullWidth onClick={onClose}>Close</Button></Grid>
            </Grid>
        </Box>
    );
}

function AnalyticsDashboard(props) {
    const { showErrorMessage, showSuccessMessage } = props;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const tokens = useMemo(() => getVizTokens(theme.palette.mode === 'dark'),
        [theme.palette.mode]);
    const username = UserProfile.getName();

    const [rangeDays, setRangeDays] = useState(90);
    const [platform, setPlatform] = useState('');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [uploadKind, setUploadKind] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        const params = { start: isoDaysAgo(rangeDays), end: todayIso() };
        if (platform) params.platform = platform;
        api.get('/pos/analytics/overview/', { params }).then((res) => {
            setData(res.data);
        }).catch((error) => {
            showErrorMessage(getErrorMessage(error));
        }).finally(() => {
            setLoading(false);
            setHasLoadedOnce(true);
        });
    }, [rangeDays, platform, showErrorMessage]);

    useEffect(() => { load(); }, [load]);

    const health = data?.health;
    const revenue = data?.revenue;
    const leakage = data?.leakage;
    const ratings = data?.ratings;
    const payouts = data?.payouts;
    const items = data?.items;
    const expenses = data?.expenses;
    const actions = data?.actions;

    const hasOrders = Boolean(revenue && revenue.total_orders > 0);

    const chartMargin = { left: isMobile ? 52 : 72, right: 16, top: 16, bottom: 44 };
    const axisSx = {
        '&& .MuiChartsAxis-line': { stroke: tokens.axis },
        '&& .MuiChartsAxis-tick': { stroke: tokens.axis },
        '&& .MuiChartsAxis-tickLabel': { fill: tokens.textMuted, fontSize: 11 },
        '&& .MuiChartsGrid-line': { stroke: tokens.grid, strokeDasharray: 'none' },
        '&& .MuiBarLabel-root': { fill: tokens.textSecondary, fontSize: 11 },
    };

    function renderFilters() {
        return (
            // Sticky on phone: the page is long, and losing sight of which
            // period you are looking at makes every number below ambiguous.
            <Paper
                variant="outlined"
                sx={{
                    p: { xs: 1, sm: 1.5 }, mb: 2,
                    position: { xs: 'sticky', md: 'static' },
                    top: 0, zIndex: 3,
                    backgroundColor: 'background.paper',
                }}
            >
                <Grid container spacing={1} alignItems="center">
                    <Grid size={{ xs: 12, sm: "auto" }}>
                        <ButtonGroup size="small" fullWidth={isMobile}>
                            {RANGE_PRESETS.map((preset) => (
                                <Button key={preset.days}
                                    variant={rangeDays === preset.days ? 'contained' : 'outlined'}
                                    onClick={() => setRangeDays(preset.days)}>
                                    {preset.label}
                                </Button>
                            ))}
                        </ButtonGroup>
                    </Grid>
                    <Grid size={{ xs: 12, sm: "auto" }}>
                        <Select size="small" displayEmpty value={platform}
                            onChange={(e) => setPlatform(e.target.value)}
                            fullWidth={isMobile} sx={{ minWidth: { sm: 150 } }}>
                            <MenuItem value="">All platforms</MenuItem>
                            {PLATFORMS.map((name) => (
                                <MenuItem key={name} value={name}>{name}</MenuItem>
                            ))}
                        </Select>
                    </Grid>
                    {/* Spacer only where there is a row to spread: on a phone it
                        consumed a whole line and split the upload pair across
                        two rows. */}
                    <Grid size="grow" sx={{ display: { xs: 'none', sm: 'block' } }} />
                    {/* Uploads are occasional, so on phone they sit as a pair of
                        half-width buttons rather than taking a row each. */}
                    <Grid size={{ xs: 6, sm: "auto" }}>
                        <Button size="small" variant="outlined" fullWidth={isMobile}
                            onClick={() => setUploadKind('order')}>
                            Orders
                        </Button>
                    </Grid>
                    <Grid size={{ xs: 6, sm: "auto" }}>
                        <Button size="small" variant="outlined" fullWidth={isMobile}
                            onClick={() => setUploadKind('payout')}>
                            Payouts
                        </Button>
                    </Grid>
                </Grid>
                {data?.range && (
                    <Typography variant="caption"
                        sx={{ color: tokens.textMuted, mt: 0.5, display: 'block' }}>
                        {data.range.start} to {data.range.end} (IST)
                    </Typography>
                )}
            </Paper>
        );
    }

    // ---------------- 1. Financial health --------------------------------
    function renderHealth() {
        return (
            <>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid size={{ xs: 6, md: 3 }}>
                        <StatTile tokens={tokens} label="Money actually received"
                            value={health.estimated_payout === null ? '--'
                                : formatCurrency(health.estimated_payout)}
                            sub={health.is_estimate ? 'estimated' : 'from payout statements'} />
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                        <StatTile tokens={tokens} label="Estimated net profit"
                            value={health.estimated_net_profit === null ? '--'
                                : formatCurrency(health.estimated_net_profit)}
                            sub="before rent, salaries, utilities"
                            tone={health.estimated_net_profit === null ? undefined
                                : health.estimated_net_profit < 0 ? 'critical' : 'good'} />
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                        <StatTile tokens={tokens} label="Net margin"
                            value={health.net_margin_pct === null ? '--'
                                : `${health.net_margin_pct}%`}
                            sub="of money received"
                            tone={health.net_margin_pct !== null && health.net_margin_pct < 0
                                ? 'critical' : undefined} />
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                        <StatTile tokens={tokens} label="Food cost"
                            value={health.food_cost_vs_payout_pct === null ? '--'
                                : `${health.food_cost_vs_payout_pct}%`}
                            sub="of money received"
                            tone={health.food_cost_vs_payout_pct !== null
                                && health.food_cost_vs_payout_pct > 40 ? 'critical' : undefined} />
                    </Grid>
                </Grid>

                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, lg: 5 }}>
                        <MoneyWaterfall health={health} tokens={tokens} />
                    </Grid>
                    <Grid size={{ xs: 12, lg: 7 }}>{renderRevenueTrend()}</Grid>
                    <Grid size={{ xs: 12, md: 6 }}>{renderPlatformMix()}</Grid>
                    <Grid size={{ xs: 12, md: 6 }}>{renderPayoutComparison()}</Grid>
                </Grid>
            </>
        );
    }

    function renderRevenueTrend() {
        const days = revenue.by_day;
        return (
            <ChartCard title="Order value by day" subtitle="Delivered orders, IST"
                tokens={tokens}
                tableHead={['Date', 'Order value', 'Orders']}
                tableRows={days.map((d) => [d.date, formatFullCurrency(d.revenue), d.orders])}>
                {days.length === 0 ? (
                    <Typography variant="body2" sx={{ color: tokens.textMuted, py: 6, textAlign: 'center' }}>
                        No delivered orders in this range.
                    </Typography>
                ) : (
                    <LineChart height={300} margin={chartMargin}
                        series={[{ data: days.map((d) => d.revenue), color: tokens.series,
                            curve: 'linear', showMark: days.length <= 31,
                            valueFormatter: (v) => formatFullCurrency(v), label: 'Order value' }]}
                        xAxis={[{ scaleType: 'point', data: days.map((d) => shortDate(d.date)),
                            tickLabelStyle: { fontSize: 10, fill: tokens.textMuted } }]}
                        yAxis={[{ valueFormatter: (v) => formatCurrency(v) }]}
                        grid={{ horizontal: true }}
                        slotProps={{ legend: { hidden: true } }}
                        sx={{ ...axisSx,
                            '& .MuiLineElement-root': { strokeWidth: 2 },
                            '& .MuiMarkElement-root': { stroke: tokens.surface, strokeWidth: 2, r: 4 } }} />
                )}
            </ChartCard>
        );
    }

    function renderPlatformMix() {
        const rows = revenue.by_platform;
        return (
            <ChartCard title="Platform mix" subtitle="Order value and average order size"
                tokens={tokens}
                tableHead={['Platform', 'Order value', 'Orders', 'Avg order']}
                tableRows={rows.map((r) => [r.platform, formatFullCurrency(r.revenue),
                    r.orders, formatFullCurrency(r.average_order_value)])}>
                {rows.length === 0 ? (
                    <Typography variant="body2" sx={{ color: tokens.textMuted, py: 6, textAlign: 'center' }}>
                        No delivered orders in this range.
                    </Typography>
                ) : isMobile ? (
                    // Three platforms in a column chart leaves most of a phone's
                    // width as band gaps; ranked rows use all of it. The bar
                    // still carries the platform's own colour, so identity is
                    // consistent with every other chart on the page.
                    <RankedBarList
                        rows={rows.map((r) => ({
                            label: r.platform, value: r.revenue,
                            sub: `${r.orders} orders · ${formatCurrency(r.average_order_value)} avg`,
                        }))}
                        tokens={tokens}
                        colorFor={(row) => tokens.platformColor(row.label)}
                        valueFormatter={(v) => formatCurrency(v)} />
                ) : (
                    <>
                        {/* Capped width: gap ratios are proportional, so on a wide
                            monitor three bars stretched to 38px - past the thin-mark
                            spec. A three-bar chart does not need 800px. */}
                        <Box sx={{ maxWidth: categoryChartWidth(rows.length), mx: 'auto' }}>
                            <BarChart height={280} margin={chartMargin}
                                dataset={rows.map((r) => ({ platform: r.platform, revenue: r.revenue }))}
                                series={[{ dataKey: 'revenue', label: 'Order value',
                                    valueFormatter: (v) => formatFullCurrency(v) }]}
                                xAxis={[{ scaleType: 'band', dataKey: 'platform', categoryGapRatio: CATEGORY_GAP,
                                    colorMap: { type: 'ordinal', values: rows.map((r) => r.platform),
                                        colors: rows.map((r) => tokens.platformColor(r.platform)) } }]}
                                yAxis={[{ valueFormatter: (v) => formatCurrency(v) }]}
                                grid={{ horizontal: true }} borderRadius={4}
                                barLabel={(item) => formatCurrency(rows[item.dataIndex]?.revenue)}
                                slotProps={{ legend: { hidden: true } }} sx={axisSx} />
                        </Box>
                        <Legend items={rows.map((r) => ({ label: r.platform,
                            color: tokens.platformColor(r.platform) }))} tokens={tokens} />
                    </>
                )}
            </ChartCard>
        );
    }

    function renderPayoutComparison() {
        const rows = payouts?.by_platform || [];
        const legendItems = [
            { label: 'Order value', color: tokens.categorical[0] },
            { label: 'Actually paid', color: tokens.categorical[2] },
        ];
        return (
            <ChartCard title="What you billed vs what you were paid"
                subtitle="Matched orders only"
                tokens={tokens}
                tableHead={['Platform', 'Matched', 'Order value', 'Paid', 'Kept %']}
                tableRows={rows.map((r) => [r.platform, r.matched_orders,
                    formatFullCurrency(r.our_revenue), formatFullCurrency(r.net_payout),
                    r.take_rate_pct === null ? '--'
                        : `${r.take_rate_pct}%${r.reliable ? '' : ' (low sample)'}`])}>
                {rows.length === 0 ? (
                    <Box sx={{ py: 5, textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ color: tokens.textMuted }}>
                            No payout statements uploaded for this period.
                        </Typography>
                        <Button size="small" sx={{ mt: 1 }} onClick={() => setUploadKind('payout')}>
                            Upload payouts
                        </Button>
                    </Box>
                ) : isMobile ? (
                    // Paired bars per platform are unreadable at phone width.
                    // Each platform gets its own block: billed, paid, and the
                    // share kept - the number the comparison exists to show.
                    <Box>
                        {rows.map((r) => (
                            <Box key={r.platform} sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center',
                                    justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <Box sx={{ width: 10, height: 10, borderRadius: '2px',
                                            backgroundColor: tokens.platformColor(r.platform) }} />
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {r.platform}
                                        </Typography>
                                    </Box>
                                    <Chip size="small" variant="outlined"
                                        color={r.reliable ? 'default' : 'warning'}
                                        label={r.take_rate_pct === null ? '--'
                                            : `${r.take_rate_pct}% kept${r.reliable ? '' : ' ?'}`} />
                                </Box>
                                <RankedBarList
                                    rows={[
                                        { label: 'You billed', value: r.our_revenue },
                                        { label: 'You were paid', value: r.net_payout },
                                    ]}
                                    max={r.our_revenue}
                                    tokens={tokens}
                                    colorFor={(row) => row.label === 'You billed'
                                        ? tokens.categorical[0] : tokens.categorical[2]}
                                    valueFormatter={(v) => formatCurrency(v)} />
                                <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                                    {r.matched_orders} matched order(s)
                                    {r.reliable ? '' : ' - too few to rely on'}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <>
                        <BarChart height={280} margin={chartMargin}
                            dataset={rows.map((r) => ({ platform: r.platform,
                                ours: r.our_revenue, paid: r.net_payout }))}
                            series={[
                                { dataKey: 'ours', color: tokens.categorical[0],
                                  valueFormatter: (v) => formatFullCurrency(v) },
                                { dataKey: 'paid', color: tokens.categorical[2],
                                  valueFormatter: (v) => formatFullCurrency(v) },
                            ]}
                            xAxis={[{ scaleType: 'band', dataKey: 'platform',
                                categoryGapRatio: 0.85, barGapRatio: 0.2 }]}
                            yAxis={[{ valueFormatter: (v) => formatCurrency(v) }]}
                            grid={{ horizontal: true }} borderRadius={4}
                            slotProps={{ legend: { hidden: true } }} sx={axisSx} />
                        <Legend items={legendItems} tokens={tokens} />
                        {rows.some((r) => !r.reliable) && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                                Some platforms have too few matched orders for a reliable rate.
                            </Alert>
                        )}
                    </>
                )}
            </ChartCard>
        );
    }

    // ---------------- 2. What sells --------------------------------------
    function renderWhatSells() {
        const top = items?.top_items || [];
        const slow = items?.bottom_items || [];
        return (
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 7 }}>
                    <ChartCard title="Best sellers"
                        subtitle={`${items?.distinct_items || 0} items, ${items?.total_units || 0} units sold`}
                        tokens={tokens} height={340}
                        tableHead={['Item', 'Units', 'Orders']}
                        tableRows={top.map((i) => [i.item_name, i.units_sold, i.orders_containing])}>
                        {top.length === 0 ? (
                            <Typography variant="body2" sx={{ color: tokens.textMuted, py: 6, textAlign: 'center' }}>
                                No item data for this range.
                            </Typography>
                        ) : isMobile ? (
                            // Long dish names in a left gutter left only half the
                            // width for data at phone size.
                            <RankedBarList
                                rows={top.map((i) => ({
                                    label: i.item_name, value: i.units_sold,
                                    sub: `${i.orders_containing} orders`,
                                }))}
                                tokens={tokens}
                                valueFormatter={(v) => `${v} units`} />
                        ) : (
                            <BarChart height={Math.max(240, top.length * 34 + 60)}
                                layout="horizontal"
                                margin={{ left: 190, right: 48, top: 8, bottom: 32 }}
                                dataset={top.map((i) => ({ item: i.item_name, units: i.units_sold }))}
                                series={[{ dataKey: 'units', color: tokens.magnitude,
                                    valueFormatter: (v) => `${v} units` }]}
                                yAxis={[{ scaleType: 'band', dataKey: 'item', categoryGapRatio: 0.45,
                                    tickLabelStyle: { fontSize: 11, fill: tokens.textMuted } }]}
                                xAxis={[{ tickMinStep: 1 }]}
                                grid={{ vertical: true }} borderRadius={4} barLabel="value"
                                slotProps={{ legend: { hidden: true } }} sx={axisSx} />
                        )}
                    </ChartCard>
                </Grid>

                <Grid size={{ xs: 12, md: 5 }}>
                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Slow movers
                        </Typography>
                        <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
                            Lowest-selling items in this period
                        </Typography>
                        {slow.length === 0 ? (
                            <Typography variant="body2" sx={{ color: tokens.textMuted, mt: 3 }}>
                                Nothing stands out as a slow mover.
                            </Typography>
                        ) : (
                            <Table size="small" sx={{ mt: 1 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Item</TableCell>
                                        <TableCell align="right">Units</TableCell>
                                        <TableCell align="right">Orders</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {slow.slice(0, 10).map((i) => (
                                        <TableRow key={i.item_name}>
                                            <TableCell>{i.item_name}</TableCell>
                                            <TableCell align="right"
                                                sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                                {i.units_sold}
                                            </TableCell>
                                            <TableCell align="right"
                                                sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                                {i.orders_containing}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Paper>
                </Grid>

                <Grid size={{ xs: 12, md: 5 }}>{renderRatings()}</Grid>
                <Grid size={{ xs: 12, md: 7 }}>{renderLeakage()}</Grid>
            </Grid>
        );
    }

    function renderRatings() {
        const distribution = ratings.distribution || {};
        const scores = ['1', '2', '3', '4', '5'];
        const counts = scores.map((s) => distribution[s] || 0);
        const any = counts.some((c) => c > 0);
        return (
            <ChartCard title="Ratings"
                subtitle={any ? `Average ${ratings.average_rating} from ${ratings.rated_orders} rated orders`
                    : 'No rated orders in this range'}
                tokens={tokens}
                tableHead={['Rating', 'Orders']}
                tableRows={scores.map((s, i) => [`${s} star`, counts[i]])}>
                {!any ? (
                    <Typography variant="body2" sx={{ color: tokens.textMuted, py: 6, textAlign: 'center' }}>
                        {ratings.unrated_orders} orders have no rating.
                    </Typography>
                ) : (
                    <Box sx={{ maxWidth: categoryChartWidth(scores.length), mx: 'auto' }}>
                        <BarChart height={250} margin={chartMargin}
                            dataset={scores.map((s, i) => ({ score: `${s}★`, count: counts[i] }))}
                            series={[{ dataKey: 'count', color: tokens.magnitude,
                                valueFormatter: (v) => `${v} orders` }]}
                            xAxis={[{ scaleType: 'band', dataKey: 'score', categoryGapRatio: CATEGORY_GAP }]}
                            yAxis={[{ tickMinStep: 1 }]} grid={{ horizontal: true }}
                            borderRadius={4} barLabel="value"
                            slotProps={{ legend: { hidden: true } }} sx={axisSx} />
                    </Box>
                )}
            </ChartCard>
        );
    }

    function renderLeakage() {
        const rows = leakage.by_platform;
        return (
            <ChartCard title="Discounts and penalties" subtitle="What you give away, per platform"
                tokens={tokens}
                tableHead={['Platform', 'Gross', 'Discount', 'Discount %', 'Penalty']}
                tableRows={rows.map((r) => [r.platform, formatFullCurrency(r.gross_sales),
                    formatFullCurrency(r.discount), `${r.discount_rate_pct}%`,
                    formatFullCurrency(r.rejection_penalty)])}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <StatTile tokens={tokens} label="Discount rate"
                            value={`${leakage.discount_rate_pct}%`}
                            sub={formatCurrency(leakage.total_discount)}
                            tone={leakage.discount_rate_pct > 20 ? 'critical' : undefined} />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <StatTile tokens={tokens} label="Gross sales"
                            value={formatCurrency(leakage.gross_sales)} />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <StatTile tokens={tokens} label="Rejection penalty"
                            value={formatCurrency(leakage.rejection_penalty)}
                            sub={`${leakage.rejected_orders} rejected`}
                            tone={leakage.rejection_penalty > 0 ? 'critical' : undefined} />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <StatTile tokens={tokens} label="Unsettled"
                            value={formatCurrency(payouts?.unsettled_amount || 0)}
                            sub="awaiting settlement" />
                    </Grid>
                </Grid>
            </ChartCard>
        );
    }

    // ---------------- 3. Where the money goes ----------------------------
    function renderExpenses() {
        const spendRows = expenses?.by_item || [];
        const rising = expenses?.price_moves?.rising || [];
        const falling = expenses?.price_moves?.falling || [];
        const quality = expenses?.data_quality;

        if (!spendRows.length) {
            return (
                <Paper variant="outlined">
                    <EmptyState title="No purchase bills in this period" tokens={tokens}>
                        Add bills to see where the money is going and how ingredient
                        prices are moving.
                    </EmptyState>
                </Paper>
            );
        }

        return (
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <ChartCard title="Biggest purchase costs"
                        subtitle={`${formatCurrency(expenses.total_spend)} across ${expenses.distinct_items} items`}
                        tokens={tokens} height={400}
                        tableHead={['Item', 'Spend', 'Share', 'Lines']}
                        tableRows={spendRows.map((i) => [i.item_name,
                            formatFullCurrency(i.spend), `${i.share_pct}%`, i.line_count])}>
                        {isMobile ? (
                            <RankedBarList
                                rows={spendRows.slice(0, 10).map((i) => ({
                                    label: i.item_name, value: i.spend,
                                    sub: `${i.share_pct}% of spend · ${i.line_count} purchases`,
                                }))}
                                tokens={tokens}
                                valueFormatter={(v) => formatCurrency(v)} />
                        ) : (
                            <BarChart height={Math.max(260, spendRows.length * 30 + 60)}
                                layout="horizontal"
                                margin={{ left: 170, right: 70, top: 8, bottom: 32 }}
                                dataset={spendRows.slice(0, 10).map((i) => ({
                                    item: i.item_name, spend: i.spend }))}
                                series={[{ dataKey: 'spend', color: tokens.magnitude,
                                    valueFormatter: (v) => formatFullCurrency(v) }]}
                                yAxis={[{ scaleType: 'band', dataKey: 'item', categoryGapRatio: 0.55,
                                    tickLabelStyle: { fontSize: 11, fill: tokens.textMuted } }]}
                                grid={{ vertical: true }} borderRadius={4}
                                barLabel={(item) => formatCurrency(
                                    spendRows.slice(0, 10)[item.dataIndex]?.spend)}
                                slotProps={{ legend: { hidden: true } }} sx={axisSx} />
                        )}
                    </ChartCard>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Ingredient prices
                        </Typography>
                        <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
                            Comparing earliest and latest purchases in this period
                        </Typography>

                        {rising.length === 0 && falling.length === 0 ? (
                            <Typography variant="body2" sx={{ color: tokens.textMuted, mt: 3 }}>
                                Not enough repeat purchases to detect price movement.
                            </Typography>
                        ) : isMobile ? (
                            // Four columns do not fit a phone: the change chip was
                            // being cut off by the card's overflow, which is worse
                            // than no label. Two lines per item instead.
                            <Box sx={{ mt: 1 }}>
                                {[...rising.slice(0, 6), ...falling.slice(0, 4)].map((m) => (
                                    <Box key={m.item_name} sx={{
                                        py: 1,
                                        borderBottom: `1px solid ${tokens.grid}`,
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center',
                                            justifyContent: 'space-between', gap: 1 }}>
                                            <Typography variant="body2" sx={{
                                                fontWeight: 500, minWidth: 0,
                                                overflow: 'hidden', textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}>
                                                {m.item_name}
                                            </Typography>
                                            <Chip size="small" sx={{ flexShrink: 0 }}
                                                label={`${m.change_pct > 0 ? '+' : ''}${m.change_pct}%`}
                                                color={m.change_pct > 0 ? 'error' : 'success'}
                                                variant="outlined" />
                                        </Box>
                                        <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                                            {m.earlier_price} to {m.latest_price} per {m.base_unit}
                                            {' '}· {m.observations} purchases
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Table size="small" sx={{ mt: 1 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Item</TableCell>
                                        <TableCell align="right">Was</TableCell>
                                        <TableCell align="right">Now</TableCell>
                                        <TableCell align="right">Change</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {[...rising.slice(0, 6), ...falling.slice(0, 4)].map((m) => (
                                        <TableRow key={m.item_name}>
                                            <TableCell>
                                                {m.item_name}
                                                <Typography variant="caption"
                                                    sx={{ color: tokens.textMuted, display: 'block' }}>
                                                    per {m.base_unit} · {m.observations} purchases
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                                {m.earlier_price}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                                {m.latest_price}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Chip size="small"
                                                    label={`${m.change_pct > 0 ? '+' : ''}${m.change_pct}%`}
                                                    color={m.change_pct > 0 ? 'error' : 'success'}
                                                    variant="outlined" />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}

                        {quality && quality.unusable_pct > 0 && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                {quality.lines_with_unusable_quantity} of {expenses.line_items} bill
                                lines ({quality.unusable_pct}%) have an unusable quantity, so they
                                are excluded from price comparison. Spend totals still include them.
                            </Alert>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        );
    }

    // ---------------- page ------------------------------------------------
    return (
        <AccessController onlyAdmin={false}>
            <Modal open={Boolean(uploadKind)} onClose={() => setUploadKind(null)}>
                <>
                    <UploadForm kind={uploadKind}
                        showErrorMessage={showErrorMessage}
                        showSuccessMessage={showSuccessMessage}
                        onUploaded={load}
                        onClose={() => setUploadKind(null)} />
                </>
            </Modal>

            {/* The page shell already supplies the outer gutter, so this only
                caps the line length on large screens. */}
            <Box sx={{ maxWidth: 1400, mx: 'auto', minWidth: 0 }}>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h5">Business performance</Typography>
                    <Typography variant="body2" sx={{ color: tokens.textSecondary }}>
                        Welcome {username}
                    </Typography>
                </Box>

                {renderFilters()}

                {loading && !hasLoadedOnce ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : !data ? (
                    <Paper variant="outlined">
                        <EmptyState title="Could not load analytics" tokens={tokens}>
                            Try a different date range, or check the API is reachable.
                        </EmptyState>
                    </Paper>
                ) : !hasOrders ? (
                    <Paper variant="outlined">
                        <EmptyState title="No orders in this period" tokens={tokens}>
                            Upload an order report from each platform to get started.
                            Add the payout statements too, so the figures reflect money
                            that actually reached the bank.
                        </EmptyState>
                    </Paper>
                ) : (
                    <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 120ms' }}>
                        {actions && actions.counts.critical > 0 && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {actions.counts.critical} item(s) need attention. See
                                "What to fix" at the bottom of this page.
                            </Alert>
                        )}

                        <SectionHeader tokens={tokens} title="Financial health"
                            question="What did the business actually make?" />
                        {renderHealth()}

                        <SectionHeader tokens={tokens} title="What sells, and what doesn't"
                            question="Which items earn their place on the menu?" />
                        {renderWhatSells()}

                        <SectionHeader tokens={tokens} title="Where the money goes"
                            question="What is being bought, and are prices moving?" />
                        {renderExpenses()}

                        <SectionHeader tokens={tokens} title="What to fix"
                            question="Gaps in the numbers, and what to do about them." />
                        <FindingsList actions={actions} tokens={tokens} />

                        <Typography variant="caption"
                            sx={{ color: tokens.textMuted, mt: 3, display: 'block' }}>
                            Order value counts delivered orders only. Days are bucketed in
                            IST. Profit is before rent, salaries and utilities, which this
                            system does not track.
                        </Typography>
                    </Box>
                )}
            </Box>
        </AccessController>
    );
}

export default AnalyticsDashboard;
